import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

interface NewFacilityData {
  facilityAmount: string;
  discountRate: string;
  vwapPercent: string;
  rapidPercent: string;
  expiryMonths: string;
  includeAdvance: boolean;
  advancePercentage: string;
  specialTerms: string;
}

const NewFacility: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<NewFacilityData>({
    facilityAmount: '',
    discountRate: '',
    vwapPercent: '80',
    rapidPercent: '20',
    expiryMonths: '24',
    includeAdvance: false,
    advancePercentage: '10',
    specialTerms: ''
  });
  const [errors, setErrors] = useState<Partial<NewFacilityData>>({});

  const steps = [
    { number: 1, title: 'Facility Details', icon: CurrencyDollarIcon },
    { number: 2, title: 'Terms & Pricing', icon: DocumentTextIcon },
    { number: 3, title: 'Review & Submit', icon: CheckCircleIcon }
  ];

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<NewFacilityData> = {};

    if (step === 1) {
      if (!formData.facilityAmount || parseFloat(formData.facilityAmount) <= 0) {
        newErrors.facilityAmount = 'Please enter a valid facility amount';
      }
      if (!formData.expiryMonths || parseInt(formData.expiryMonths) <= 0) {
        newErrors.expiryMonths = 'Please select expiry duration';
      }
    }

    if (step === 2) {
      if (!formData.discountRate || parseFloat(formData.discountRate) <= 0) {
        newErrors.discountRate = 'Please enter a valid discount rate';
      }
      const vwap = parseFloat(formData.vwapPercent) || 0;
      const rapid = parseFloat(formData.rapidPercent) || 0;
      if (vwap + rapid !== 100) {
        newErrors.vwapPercent = 'VWAP and Rapid percentages must equal 100%';
      }
      if (formData.includeAdvance && (!formData.advancePercentage || parseFloat(formData.advancePercentage) <= 0)) {
        newErrors.advancePercentage = 'Please enter a valid advance percentage';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleInputChange = (field: keyof NewFacilityData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleVwapRapidChange = (type: 'vwap' | 'rapid', value: string) => {
    const numValue = parseFloat(value) || 0;
    if (type === 'vwap') {
      setFormData(prev => ({
        ...prev,
        vwapPercent: value,
        rapidPercent: (100 - numValue).toString()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        rapidPercent: value,
        vwapPercent: (100 - numValue).toString()
      }));
    }
  };

  const handleSubmit = async () => {
    // Here you would submit the new facility request
    console.log('Submitting new facility:', formData);
    // For now, just navigate to dashboard
    navigate('/dashboard');
  };

  const formatCurrency = (value: string) => {
    const num = parseFloat(value.replace(/[^0-9]/g, ''));
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Start New Facility</h1>
                <p className="text-gray-600 mt-1">Request a new equity facility</p>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <SparklesIcon className="h-6 w-6 text-navy-700" />
              <span className="text-navy-700 font-medium">New Facility</span>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <div className={`flex items-center ${index !== steps.length - 1 ? 'flex-1' : ''}`}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    step.number < currentStep
                      ? 'bg-green-500 text-white'
                      : step.number === currentStep
                      ? 'bg-navy-700 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step.number < currentStep ? (
                    <CheckCircleIcon className="h-6 w-6" />
                  ) : (
                    <step.icon className="h-6 w-6" />
                  )}
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${
                    step.number <= currentStep ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </p>
                </div>
              </div>
              {index !== steps.length - 1 && (
                <div className={`w-24 h-1 mx-4 rounded ${
                  step.number < currentStep ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Content */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
        >
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Facility Details</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Facility Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="text"
                    value={formatCurrency(formData.facilityAmount)}
                    onChange={(e) => handleInputChange('facilityAmount', e.target.value.replace(/[^0-9]/g, ''))}
                    className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500 ${
                      errors.facilityAmount ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="0"
                  />
                </div>
                {errors.facilityAmount && (
                  <p className="mt-1 text-sm text-red-600">{errors.facilityAmount}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facility Duration
                </label>
                <select
                  value={formData.expiryMonths}
                  onChange={(e) => handleInputChange('expiryMonths', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500 ${
                    errors.expiryMonths ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="12">12 months</option>
                  <option value="18">18 months</option>
                  <option value="24">24 months</option>
                  <option value="36">36 months</option>
                </select>
                {errors.expiryMonths && (
                  <p className="mt-1 text-sm text-red-600">{errors.expiryMonths}</p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Current Facility Status</h4>
                <p className="text-sm text-blue-700">
                  Your current facility has $150,000,000 available and expires on December 31, 2025.
                  The new facility will begin after the current one expires.
                </p>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Terms & Pricing</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Rate
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.discountRate}
                    onChange={(e) => handleInputChange('discountRate', e.target.value)}
                    className={`w-full pr-8 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500 ${
                      errors.discountRate ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="0.0"
                    step="0.1"
                    min="0"
                    max="10"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                </div>
                {errors.discountRate && (
                  <p className="mt-1 text-sm text-red-600">{errors.discountRate}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  VWAP / Rapid Split
                </label>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600">VWAP Purchase</span>
                      <span className="text-sm font-medium">{formData.vwapPercent}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.vwapPercent}
                      onChange={(e) => handleVwapRapidChange('vwap', e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600">Rapid Purchase</span>
                      <span className="text-sm font-medium">{formData.rapidPercent}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.rapidPercent}
                      onChange={(e) => handleVwapRapidChange('rapid', e.target.value)}
                      className="w-full"
                    />
                  </div>
                </div>
                {errors.vwapPercent && (
                  <p className="mt-1 text-sm text-red-600">{errors.vwapPercent}</p>
                )}
              </div>

              <div>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.includeAdvance}
                    onChange={(e) => handleInputChange('includeAdvance', e.target.checked)}
                    className="h-4 w-4 text-navy-700 focus:ring-navy-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Include Advance Draw</span>
                </label>
                {formData.includeAdvance && (
                  <div className="mt-3 ml-7">
                    <label className="block text-sm text-gray-600 mb-1">
                      Advance Percentage
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={formData.advancePercentage}
                        onChange={(e) => handleInputChange('advancePercentage', e.target.value)}
                        className={`w-full pr-8 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500 ${
                          errors.advancePercentage ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="10"
                        step="1"
                        min="1"
                        max="20"
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                    </div>
                    {errors.advancePercentage && (
                      <p className="mt-1 text-sm text-red-600">{errors.advancePercentage}</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Terms or Conditions (Optional)
                </label>
                <textarea
                  value={formData.specialTerms}
                  onChange={(e) => handleInputChange('specialTerms', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                  rows={3}
                  placeholder="Enter any special terms or conditions..."
                />
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Review & Submit</h2>
              
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <h3 className="font-semibold text-gray-900 mb-4">Facility Summary</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Facility Amount</p>
                    <p className="font-semibold text-gray-900">
                      ${formatCurrency(formData.facilityAmount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Duration</p>
                    <p className="font-semibold text-gray-900">{formData.expiryMonths} months</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Discount Rate</p>
                    <p className="font-semibold text-gray-900">{formData.discountRate}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">VWAP / Rapid Split</p>
                    <p className="font-semibold text-gray-900">
                      {formData.vwapPercent}% / {formData.rapidPercent}%
                    </p>
                  </div>
                  {formData.includeAdvance && (
                    <div>
                      <p className="text-sm text-gray-500">Advance Draw</p>
                      <p className="font-semibold text-gray-900">
                        {formData.advancePercentage}% (${formatCurrency(
                          (parseFloat(formData.facilityAmount) * parseFloat(formData.advancePercentage) / 100).toString()
                        )})
                      </p>
                    </div>
                  )}
                </div>

                {formData.specialTerms && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500 mb-1">Special Terms</p>
                    <p className="text-gray-900">{formData.specialTerms}</p>
                  </div>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CalendarIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-900">Next Steps</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      After submission, our team will review your request and prepare the facility agreement.
                      You'll receive an email with the agreement for e-signature within 1-2 business days.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back
            </button>

            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-2 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors"
            >
              {currentStep === 3 ? 'Submit Request' : 'Next'}
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default NewFacility; 